import { createMuiTheme } from "@material-ui/core";

const DBG_BLUE = "#009";
const DBG_BLACK = "#333";

/*
 * Default Theme: https://material-ui.com/customization/default-theme/
 * Global theme variation: https://material-ui.com/customization/components/#5-global-theme-variation
 * Globals: https://material-ui.com/customization/globals/
 * Palette: https://material-ui.com/customization/palette/
 */
export const theme = createMuiTheme({
    overrides: {
        MuiAppBar: {
            colorDefault: {
                backgroundColor: "#fff"
            },
            root: {
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0
            }
        },
        MuiBreadcrumbs: {
            root: {
                color: DBG_BLACK,
                fontSize: "0.875rem"
            }
        },
        MuiButton: {
            contained: {
                borderStyle: "solid",
                borderWidth: 1
            },
            containedPrimary: {
                borderColor: DBG_BLUE,
                "&:hover": {
                    backgroundColor: "#fff",
                    color: DBG_BLUE
                }
            }
        },
        MuiIconButton: {
            root: {
                color: DBG_BLUE,
                "&:hover": {
                    backgroundColor: "inherit"
                }
            }
        },
        MuiInputBase: {
            input: {
                boxSizing: "inherit",
                paddingBottom: 0,
                paddingLeft: 12,
                paddingRight: 12,
                paddingTop: 0,
                background: "#fff"
            },
            root: {
                fontSize: "0.9375rem",
                width: "100%"
            }
        },
        MuiLink: {
            underlineHover: {
                color: DBG_BLUE,
                cursor: "pointer",
                "&:hover": {
                    color: DBG_BLUE
                }
            }
        },
        MuiOutlinedInput: {
            adornedEnd: {
                paddingRight: 0,
                background: "#fff"
            }
        },
        MuiSvgIcon: {
            root: {
                width: "0.75em",
                height: "0.75em"
            }
        },
        MuiRadio: {
            colorSecondary: {
                "&$checked": {
                    color: DBG_BLUE
                }
            }
        },
        MuiCheckbox: {
            colorSecondary: {
                "&$checked": {
                    color: DBG_BLUE
                }
            }
        },
        MuiPaper: {
            root:{
                "&$outlined": {
                    border: "none",
                }
            },
            elevation8: {
                backgroundColor: 'white'
            }
        },
        MuiFormHelperText: {
            contained: {
                marginTop: 7,
                marginBottom: 7,
            }
        }
    },
    palette: {
        background: {default: "#fff", paper: "#f0f0f0"},
        common: {black: DBG_BLACK},
        primary: {main: DBG_BLUE},
        secondary: {main: "#e6e6e6"},
        error: {main: "#f00"},
        info: {main: "#d7ebff"},
        success: {main: "#51af31"}
    },
    props: {
        MuiAppBar: {
            color: "default",
            elevation: 0,
            position: "sticky"
        },
        MuiButton: {
            color: "primary",
            disableElevation: true,
            disableRipple: true,
            variant: "contained"
        },
        MuiIconButton: {
            disableRipple: true,
            size: "small"
        },
        MuiPaper: {
            elevation: 0,
        },
        MuiCard: {
            variant: "outlined",
        }
    },
    shape: {
        borderRadius: 0,
    },
    typography: {
        fontFamily: "newsGothic_light, Arial, Verdana, Helvetica, sans-serif",
        fontSize: 17,
        h1: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "2rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.2
        },
        h2: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_light, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "2rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.2
        },
        h3: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "1.125rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.5
        },
        h4: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "1rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.5
        },
        h5: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "0.9375rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.5
        },
        h6: {
            color: DBG_BLUE,
            fontFamily: "newsGothic_light, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "0.9375rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.5
        },
        subtitle1: {
            color: DBG_BLACK,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "1.125rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.2
        },
        subtitle2: {
            color: DBG_BLACK,
            fontFamily: "newsGothic_demi, Arial, Verdana, Helvetica, sans-serif",
            fontSize: "1rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.2
        },
        body1: {
            color: DBG_BLACK,
            fontSize: "1.0625rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: "normal"
        },
        body2: {
            color: DBG_BLACK,
            fontSize: "0.875rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: "normal"
        },
        button: {
            fontSize: "1.0625rem",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: 1.4,
            paddingBottom: 5,
            paddingLeft: 17,
            paddingRight: 17,
            paddingTop: 5,
            textTransform: "none"
        },
        caption: {
            color: DBG_BLACK,
            fontSize: "smaller",
            fontWeight: 400,
            letterSpacing: "normal",
            lineHeight: "normal"
        },
        overline: {
            textTransform: "none"
        }
    }
});

export default theme;
